import { tokens } from '@/plugins/vuetify/tokens'
import { DateTime } from 'luxon'
import tinygradient from 'tinygradient'

const perf = tokens.color.data.performance

/** The three-step semantic scale a metric card takes from a 0–100 score. */
export type MetricScoreColor = 'success' | 'warning' | 'danger'

/**
 * NOTE: the `inverse` parameter is accepted throughout this composable but is not
 * implemented anywhere — `getRanges`/`getExtendedRanges` return a fixed scale and
 * `getColor` switches on the raw rating. `getColor` likewise ignores `maxValue`.
 * ~36 call sites pass these arguments, so every `inverse` / `inverseColor` prop
 * downstream (WRatingsBlock, HeatmapCell, WLegend) is currently inert. The
 * parameters are underscore-prefixed rather than removed so that caller intent
 * stays visible. Implementing inversion is a cross-component behaviour change and
 * needs its own ticket; surfaced by TE-14403.
 */
export function useRatingColors() {
  function getRanges(_inverse = false) {
    return [0, 0.167, 0.333, 0.5, 0.667, 0.833, 1]
  }

  function getExtendedRanges(_inverse = false) {
    return [0, 0.1, 0.167, 0.25, 0.333, 0.417, 0.5, 0.583, 0.667, 0.75, 0.833, 0.917, 1]
  }

  function getColor(value: number, _maxValue = 1, _inverse = false) {
    const rating = Math.floor(Number(value))
    switch (rating) {
      case 1:
        return perf.critical       // #DA1E28
      case 2:
        return perf.poor           // #E76B35
      case 3:
        return perf.satisfactory   // #F4A721
      case 4:
        return perf.good           // #26C255
      case 5:
        return perf.excellent      // #1C903F
      default:
        return perf.null           // #BEBEBE
    }
  }

  function getHeatMapColor(value: number, maxValue = 1, _inverse = false) {
    if (value == null || !maxValue || value > maxValue) {
      return perf.null
    }

    const heatmapGradient = tinygradient([
      { color: perf.critical, pos: 0 },
      { color: perf.poor, pos: 0.167 },
      { color: perf.concerning, pos: 0.333 },
      { color: perf.neutral, pos: 0.5 },
      { color: perf.satisfactory, pos: 0.667 },
      { color: perf.good, pos: 0.833 },
      { color: perf.excellent, pos: 1 }
    ])
    value = value / maxValue
    const color = heatmapGradient.rgbAt(value)
    return color.toHexString()
  }

  function getScoreColor(value: number, maxValue = 1, inverse = false, showZero = false) {
    if (value == null || !maxValue || value > maxValue) {
      return perf.null
    }

    if (value <= 0 && !showZero) {
      return perf.null
    }

    if (inverse && value != 0) {
      if (value == maxValue) {
        value = maxValue / maxValue
      } else {
        value = maxValue - value
      }
    }

    if (showZero && value == 0) {
      if (inverse) {
        value = maxValue
      } else {
        value = 1
      }
    }

    // 7-tier breakpoints:
    // critical=0–14%, poor=15–29%, concerning=30–44%,
    // neutral=45–59%, satisfactory=60–74%, good=75–89%, excellent=90–100%
    const pct = (value / maxValue) * 100
    if (pct < 15) return perf.critical        // 0–14%
    if (pct < 30) return perf.poor            // 15–29%
    if (pct < 45) return perf.concerning      // 30–44%
    if (pct < 60) return perf.neutral         // 45–59%
    if (pct < 75) return perf.satisfactory    // 60–74%
    if (pct < 90) return perf.good            // 75–89%
    return perf.excellent                     // 90–100%
  }

  const calculateColorClass = (startDate: string, endDate: string): string => {
    const difference = DateTime.fromISO(endDate).diff(DateTime.fromISO(startDate), 'minutes').minutes
    const score = getScoreFromDiff(difference)
    return getScoreColor(score, 100)
  }

  function getDeltaColor(delta: number): string {
    const clamped = Math.min(Math.abs(delta), 1)
    if (delta === 0) return '#999'

    const positiveGradient = tinygradient([
      { color: '#93c59a', pos: 0 },
      { color: perf.excellent, pos: 0.15 },
    ])

    const negativeGradient = tinygradient([
      { color: '#f76767', pos: 0 },
      { color: perf.critical, pos: 0.15 },
    ])

    if (delta < 0) return negativeGradient.rgbAt(clamped).toHexString()
    return positiveGradient.rgbAt(clamped).toHexString()
  }

  const getScoreFromDiff = (difference: number): number => {
    let score: number

    if (difference <= 480) {
      score = 100
    } else if (difference <= 720) {
      score = 99
    } else if (difference <= 1200) {
      score = 94
    } else if (difference <= 1320) {
      score = 89
    } else if (difference <= 1440) {
      score = 84
    } else if (difference <= 1680) {
      score = 79
    } else if (difference <= 1820) {
      score = 69
    } else if (difference <= 2000) {
      score = 59
    } else if (difference <= 2240) {
      score = 49
    } else if (difference <= 2880) {
      score = 24
    } else {
      score = 1
    }

    return score
  }

  // Metric cards read a 0–100 score (e.g. from getScoreFromDiff) on the same three steps.
  const getMetricColorFromScore = (score: number): MetricScoreColor => {
    if (score < 45) return 'danger'
    if (score < 75) return 'warning'
    return 'success'
  }

  return {
    getColor,
    getRanges,
    getHeatMapColor,
    getExtendedRanges,
    getScoreColor,
    getScoreFromDiff,
    getMetricColorFromScore,
    getDeltaColor,
    calculateColorClass
  }
}
